import { useCallback, useEffect, useState } from 'react';

import { hasMobileDesignParam, isMobileWaitSession } from 'Utils/mobile';
import { LoggerFactory, DOMAIN, FEATURE } from 'Services/Logger';
import { EMAIL_KEY, KUESKI_SESSION_STORAGE_KEY } from 'Src/consts/session';
import { EVENTS, isFlutterInAppWebEnabled, sendHandleSaveSession } from 'Utils/events/flutterWebView';

const logger = LoggerFactory.getLogger(DOMAIN.MOBILE);

export const useFlutterWebView = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);

  const isFlutterEnabled = isFlutterInAppWebEnabled();
  const isMobileApp = hasMobileDesignParam() && isFlutterEnabled;
  const isMobileWait = isMobileWaitSession();

  const sessionId = localStorage.getItem(KUESKI_SESSION_STORAGE_KEY);
  const email = localStorage.getItem(EMAIL_KEY);
  const debugValues = {
    email: Boolean(email),
    isFlutterEnabled,
    isLoading,
    isMobileApp,
    isMobileWait,
    isReady,
    sessionId: Boolean(sessionId),
  };
  const { flutterInAppWebViewPlatformReady, handleSessionSaved } = EVENTS;

  const completeWebviewLoading = useCallback(() => {
    setIsLoading(false);
    setIsReady(true);
  }, []);

  /**
   * 1. Active with wait_mobile_session query param only.
   * 2. Receive flutterInAppWebViewPlatformReady event from app.
   * 3. Send saveSession handler to the app.
   * 4. Receive handleSessionSaved event from app
   */
  useEffect(() => {
    /**
       * Skipping this hook when wait_mobile_session doesn't exists
       * instead the second useEffect start.
       */
    if (!isMobileWait) {
      logger.info(
        'FlutterInAppWebView validation skipped, wait_mobile_session query param exists',
        `${DOMAIN.MOBILE}.${FEATURE.LOGIN}`,
        debugValues,
      );
      return () => null;
    }

    if (!isMobileApp) {
      logger.info(
        'FlutterInAppWebView validation skipped, is not mobile app',
        `${DOMAIN.MOBILE}.${FEATURE.LOGIN}`,
        debugValues,
      );
      completeWebviewLoading();
      return () => completeWebviewLoading();
    }

    /**
       * * Note: It is in case the event does not respond
       */
    const MS = 1000 * 5; // 5s
    const TIMEOUT = setTimeout(() => {
      logger.warn(
        `${flutterInAppWebViewPlatformReady} event did not respond in time`,
        `${DOMAIN.MOBILE}.${FEATURE.LOGIN}`,
        debugValues,
      );
      completeWebviewLoading();
    }, MS);

    const handleFlutterInAppWebViewPlatformReadyEvent = () => {
      clearTimeout(TIMEOUT);
      logger.info(
        `FlutterInAppWebView ${flutterInAppWebViewPlatformReady} event responded`,
        `${DOMAIN.MOBILE}.${FEATURE.LOGIN}`,
        debugValues,
      );
      sendHandleSaveSession();
    };

    const handleSessionSavedEvent = () => {
      clearTimeout(TIMEOUT);
      logger.info(
        `FlutterInAppWebView ${handleSessionSaved} event responded`,
        `${DOMAIN.MOBILE}.${FEATURE.LOGIN}`,
        debugValues,
      );
      completeWebviewLoading();
    };

    window.addEventListener(
      flutterInAppWebViewPlatformReady,
      handleFlutterInAppWebViewPlatformReadyEvent,
    );

    window.addEventListener(
      handleSessionSaved,
      handleSessionSavedEvent,
    );

    return () => {
      window.removeEventListener(
        flutterInAppWebViewPlatformReady,
        handleFlutterInAppWebViewPlatformReadyEvent,
      );

      window.removeEventListener(
        handleSessionSaved,
        handleSessionSavedEvent,
      );
    };
  }, [isMobileApp, isMobileWait]);

  useEffect(() => {
    /**
     * Using isMobileWait to skip this hook
     * when wait_mobile_session query param exists.
     * Instead the first useEffect start.
     */
    if (isMobileWait) return () => null;

    if (!isMobileApp) {
      logger.info(
        'FlutterInAppWebView validation skipped',
        `${DOMAIN.MOBILE}.${FEATURE.LOGIN}`,
        debugValues,
      );
      completeWebviewLoading();
      return () => completeWebviewLoading();
    }

    /**
     * * Note: It is in case the event does not respond
     */
    const MS = 1000 * 5; // 5s
    const TIMEOUT = setTimeout(() => {
      logger.warn(
        `${flutterInAppWebViewPlatformReady} event did not respond in time`,
        `${DOMAIN.MOBILE}.${FEATURE.LOGIN}`,
        debugValues,
      );
      completeWebviewLoading();
    }, MS);

    const handleFlutterInAppWebViewPlatformReadyEvent = () => {
      clearTimeout(TIMEOUT);
      logger.info(
        `FlutterInAppWebView ${flutterInAppWebViewPlatformReady} event responded`,
        `${DOMAIN.MOBILE}.${FEATURE.LOGIN}`,
        debugValues,
      );
      completeWebviewLoading();
    };

    window.addEventListener(
      flutterInAppWebViewPlatformReady,
      handleFlutterInAppWebViewPlatformReadyEvent,
    );

    return () => {
      window.removeEventListener(
        flutterInAppWebViewPlatformReady,
        handleFlutterInAppWebViewPlatformReadyEvent,
      );
    };
  }, [isMobileApp, isMobileWait]);

  return {
    isLoading,
    isMobileApp,
    isReady,
  };
};
